import { useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Icon,
  InlineStack,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Strong,
  Text,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';

export type ToggleStatusModalData = {
  name: string;
  /** Whether the sync is running now, so the modal offers to pause it; otherwise to activate it. */
  isActive: boolean;
  /** What the sync does, in the progressive form ("syncing people to …"). */
  summary: ReactNode;
  /** Pauses or activates the sync; resolves to whether it went through. */
  onToggle: () => Promise<boolean>;
};

/** Ember's `confirm-and-toggle-status`: what starts or stops, what is kept, and the question. Managed modal. */
export function ToggleStatusModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<ToggleStatusModalData>) {
  const { name, isActive, summary, onToggle } = data;
  const [isToggling, setIsToggling] = useState(false);
  const verb = isActive ? 'Pause' : 'Activate';

  const handleToggle = () => {
    setIsToggling(true);
    void onToggle().then((toggled) => {
      setIsToggling(false);
      if (toggled) {
        onConfirm();
      }
    });
  };

  return (
    <Modal
      state={modalState}
      title={`${verb} ${name}`}
      onClose={onClose}
      isDismissable={!isToggling}
      data-test-sql-imports-modal="confirm-and-toggle-status"
    >
      <ModalBody>
        {isActive ? (
          <>
            <InlineStack gap="100" alignBlock="start">
              <Icon name="critical" color="critical" />
              <Box>
                <Text>Stop</Text> {summary}
              </Box>
            </InlineStack>
            <InlineStack gap="100" alignBlock="start" mt="200">
              <Icon name="check-circle" color="success" />
              <Paragraph>Preserve database settings and historical imports</Paragraph>
            </InlineStack>
          </>
        ) : (
          <InlineStack gap="100" alignBlock="start">
            <Icon name="check-circle" color="success" />
            <Box>
              <Text>Start</Text> {summary}
            </Box>
          </InlineStack>
        )}
        <Paragraph mt="200">
          Are you sure you want to {verb.toLowerCase()} <Strong>{name}</Strong>?
        </Paragraph>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onClose} isDisabled={isToggling}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isLoading={isToggling}
          onClick={handleToggle}
          data-test-sql-import-button="confirm-toggle-status"
        >
          {verb} sync
        </Button>
      </ModalFooter>
    </Modal>
  );
}
